import { EmailSchema } from '@triedge/domain-core/value-objects';

export type ValidationIndicatorState = 'default' | 'missing' | 'invalid';

const PHONE_ALLOWED_REGEX = /^\+?[\d\s().-]+$/;

export const readContactText = (value: unknown): string => String(value ?? '').trim();

export const normalizePhoneInput = (value: string): string => {
  const filtered = value.replace(/[^\d\s().+\-]/g, '');
  let normalized = '';
  let sawLeadingPlus = false;

  for (const character of filtered) {
    if (character === '+') {
      if (!sawLeadingPlus && normalized.length === 0) {
        normalized += character;
        sawLeadingPlus = true;
      }
      continue;
    }
    normalized += character;
  }

  return normalized;
};

export const getPhoneDigitCount = (value: unknown): number =>
  readContactText(value).replace(/\D/g, '').length;

export const isValidEmailAddress = (value: unknown): boolean => {
  const normalized = readContactText(value);
  return Boolean(normalized) && EmailSchema.safeParse(normalized).success;
};

export const isValidPhoneNumber = (value: unknown): boolean => {
  const normalized = readContactText(
    typeof value === 'string' ? normalizePhoneInput(value) : value,
  );
  return Boolean(normalized)
    && PHONE_ALLOWED_REGEX.test(normalized)
    && getPhoneDigitCount(normalized) >= 10;
};

export const normalizePhoneForSubmission = (value: unknown): string | undefined => {
  const normalized = readContactText(
    typeof value === 'string' ? normalizePhoneInput(value) : value,
  );
  return isValidPhoneNumber(normalized) ? normalized : undefined;
};

export const pickFirstValidEmail = (values: unknown[]): string | undefined => {
  for (const value of values) {
    if (isValidEmailAddress(value)) {
      return readContactText(value);
    }
  }
  return undefined;
};

export const pickFirstValidPhone = (values: unknown[]): string | undefined => {
  for (const value of values) {
    const normalized = normalizePhoneForSubmission(value);
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
};

export const getValidationIndicatorState = (input: {
  show: boolean;
  value: unknown;
  required?: boolean;
  validator?: (value: unknown) => boolean;
}): ValidationIndicatorState => {
  if (!input.show) {
    return 'default';
  }

  const normalized = readContactText(input.value);
  if (!normalized) {
    return input.required ? 'missing' : 'default';
  }

  if (input.validator && !input.validator(normalized)) {
    return 'invalid';
  }

  return 'default';
};

export const getValidationIndicatorClassName = (
  state: ValidationIndicatorState,
): string => {
  switch (state) {
    case 'missing':
      return 'border-amber-700 focus:border-amber-700 focus:ring-amber-700';
    case 'invalid':
      return 'border-red-600 focus:border-red-600 focus:ring-red-600';
    default:
      return '';
  }
};
